import type { IModule, ModuleContext } from '@punica/editor';
import { ModuleLifecycleState } from '@punica/editor';
import { MODULE_NAMES } from '@punica/editor';
import appBar from '../appBar/index.js';
import layoutToggles from '../layoutToggles/index.js';

/**
 * Shell AppBar module — the top strip (branding, workspace title, actions).
 *
 * Profile-gated: only profiles listing `shell.appBar` get a bar, and its
 * composition comes from that profile's `shell.appBar` spec. The id is a literal
 * because `MODULE_NAMES` lives in the substrate and this module is contributed
 * by the shell package; the profile validator accepts contributed ids.
 *
 * Depends on: kernel.commands (items dispatch command ids), shell.layout (the
 * bar reshapes the layout root's grid rows).
 */
export class AppBarModule implements IModule {
  readonly name = 'shell.appBar';
  readonly version = '1.0.0';
  readonly dependencies: readonly string[] = [
    MODULE_NAMES.KERNEL.COMMANDS,
    MODULE_NAMES.SHELL.LAYOUT
  ];

  #state: ModuleLifecycleState = ModuleLifecycleState.CREATED;

  get state(): ModuleLifecycleState {
    return this.#state;
  }

  async bootstrap(context: ModuleContext): Promise<void> {
    const { punica, registry } = context;

    const commandsState = registry.getState(MODULE_NAMES.KERNEL.COMMANDS);
    if (commandsState !== ModuleLifecycleState.BOOTSTRAPPED) {
      throw new Error(
        'kernel.commands must be bootstrapped before shell.appBar'
      );
    }

    if (!punica.shell?.Layout?.manager) {
      throw new Error('Layout manager not available');
    }
  }

  async start(context: ModuleContext): Promise<void> {
    const { punica } = context;
    let spec;
    try {
      spec =
        punica.shell?.Profile?.manager?.registry?.getActive?.()?.shell?.appBar;
    } catch {
      // No active profile (headless-ish boot) → the module's own defaults.
      spec = undefined;
    }
    appBar.manager.init(spec);
    // The bar's `Layout` item opens the region toggles; they start with the
    // bar so a profile without one has neither the popover nor its shortcuts.
    layoutToggles.start();
  }
}
